<template>
  <div class="qwen-content">
    <section class="qwen-section qwen-feature-console">
      <div class="qwen-console-copy">
        <span>Qwen Image 2.0 Pro</span>
        <h2>{{ text('mainFeaturesTitle') }}</h2>
        <p>{{ text('keyFeaturesIntro') }}</p>
        <button type="button" @click="$emit('back-to-generator')">{{ text('howToCta') }}</button>
      </div>
      <div class="qwen-feature-list">
        <button v-for="(feature, index) in features" :key="feature.name" type="button" @click="scrollTo('qwen-feature-' + index)">
          <b>0{{ index + 1 }}</b><span><strong>{{ feature.name }}</strong><small>{{ feature.short }}</small></span><i>&rarr;</i>
        </button>
      </div>
    </section>

    <section class="qwen-section qwen-negative-lab" id="qwen-feature-0" v-if="features[0]">
      <div class="qwen-section-title"><span>01 · CONTROL</span><h2>{{ features[0].name }}</h2><p>{{ features[0].desc }}</p></div>
      <div class="qwen-lab-grid">
        <article v-for="(example, index) in examples(features[0]).slice(0, 2)" :key="index">
          <div class="qwen-prompt-panel">
            <label>{{ text('promptLabel') }}</label><p>{{ positivePrompt(example.prompt) }}</p>
            <label>{{ text('negativePromptLabel') }}</label><p class="negative">{{ negativePrompt(example.prompt) }}</p>
            <button type="button" @click="$emit('use-prompt', example.prompt)">{{ $t('seo.imageModels.usePrompt') }}</button>
          </div>
          <figure><img :src="example.image" :alt="example.prompt" loading="lazy"></figure>
        </article>
      </div>
    </section>

    <section class="qwen-section qwen-enhance-section" id="qwen-feature-1" v-if="features[1]">
      <div class="qwen-enhance-copy"><span>02 · ENHANCE</span><h2>{{ features[1].name }}</h2><p>{{ features[1].desc }}</p><div class="qwen-short-prompt"><small>{{ text('shortPromptLabel') }}</small><strong>{{ featurePrompt(features[1]) }}</strong></div></div>
      <div class="qwen-enhance-results"><figure v-for="(example, index) in examples(features[1]).slice(0, 2)" :key="index"><img :src="example.image" :alt="example.prompt" loading="lazy"><figcaption>0{{ index + 1 }} · {{ example.prompt }}</figcaption></figure></div>
    </section>

    <section class="qwen-section qwen-batch-section" id="qwen-feature-2" v-if="features[2]">
      <div class="qwen-section-title qwen-section-title--center"><span>03 · VARIATIONS</span><h2>{{ features[2].name }}</h2><p>{{ features[2].desc }}</p></div>
      <div class="qwen-batch-stage">
        <div class="qwen-batch-prompt"><span>{{ $t('seo.imageModels.demoPrompt') }}</span><p>{{ featurePrompt(features[2]) }}</p><button type="button" @click="$emit('use-prompt', featurePrompt(features[2]))">{{ $t('seo.imageModels.usePrompt') }}</button></div>
        <div class="qwen-batch-grid"><figure v-for="(image, index) in batchImages" :key="index"><img :src="image" :alt="features[2].name + ' ' + (index + 1)" loading="lazy"><b>0{{ index + 1 }}</b></figure></div>
      </div>
    </section>

    <section class="qwen-section qwen-format-section" id="qwen-feature-3" v-if="features[3]">
      <div class="qwen-format-copy"><span>04 · FORMAT</span><h2>{{ features[3].name }}</h2><p>{{ features[3].desc }}</p><div class="qwen-size-list"><i>2688 × 1536</i><i>2048 × 2048</i><i>1536 × 2688</i><i>Custom</i></div></div>
      <div class="qwen-format-wall"><figure><img :src="formatImages[0]" :alt="features[3].name" loading="lazy"></figure><figure><img :src="formatImages[1]" :alt="features[3].name" loading="lazy"></figure><figure><img :src="formatImages[2]" :alt="features[3].name" loading="lazy"></figure></div>
    </section>

    <section class="qwen-section qwen-realism" id="qwen-feature-4" v-if="features[4]">
      <img :src="featureImage(features[4], 4)" :alt="features[4].name" loading="lazy">
      <div><span>05 · DETAIL</span><h2>{{ features[4].name }}</h2><p>{{ features[4].desc }}</p><button type="button" @click="$emit('use-prompt', featurePrompt(features[4]))">{{ $t('seo.imageModels.usePrompt') }}</button></div>
    </section>

    <section class="qwen-section qwen-how-to">
      <div class="qwen-how-intro"><span>{{ text('howToKicker') }}</span><h2>{{ text('howToTitle') }}</h2><p>{{ text('howToIntro') }}</p></div>
      <div class="qwen-timeline">
        <article v-for="(step, index) in howToSteps" :key="step.title"><b>0{{ index + 1 }}</b><div><h3>{{ step.title }}</h3><p>{{ step.desc }}</p></div></article>
        <button type="button" @click="$emit('back-to-generator')">{{ text('howToCta') }}</button>
      </div>
    </section>

    <section class="qwen-section qwen-prompt-gallery">
      <div class="qwen-section-title qwen-section-title--center"><span>{{ text('showcaseKicker') }}</span><h2>{{ text('showcaseTitle') }}</h2><p>{{ text('showcaseIntro') }}</p></div>
      <div class="qwen-gallery-track"><article v-for="(example, index) in galleryExamples" :key="index"><figure><img :src="example.image" :alt="example.prompt" loading="lazy"><b>0{{ index + 1 }}</b></figure><p>{{ example.prompt }}</p><button type="button" @click="$emit('use-prompt', example.prompt)">{{ $t('seo.imageModels.usePrompt') }}</button></article></div>
    </section>

    <section class="qwen-section qwen-use-cases">
      <div class="qwen-section-title"><span>{{ text('useCasesKicker') }}</span><h2>{{ text('useCasesTitle') }}</h2></div>
      <div class="qwen-use-grid"><article v-for="(item, index) in useCases" :key="item.name"><span>0{{ index + 1 }}</span><h3>{{ item.name }}</h3><p>{{ item.desc }}</p></article></div>
    </section>

    <section class="qwen-section qwen-faq">
      <div class="qwen-section-title qwen-section-title--center"><span>FAQ</span><h2>{{ text('faqTitle') }}</h2></div>
      <details v-for="faq in faqs" :key="faq.q"><summary><span>{{ faq.q }}</span><i></i></summary><p>{{ faq.a }}</p></details>
    </section>

    <section class="qwen-cta">
      <div><span>Qwen Image 2.0 Pro · Miral</span><h2>{{ text('ctaTitle') }}</h2><p>{{ text('ctaDesc') }}</p><button type="button" @click="$emit('back-to-generator')">{{ text('howToCta') }}</button></div>
      <figure><img :src="fallbackImages[3]" :alt="text('ctaTitle')" loading="lazy"><div><i v-for="index in 4" :key="index">0{{ index }}</i></div></figure>
    </section>
  </div>
</template>

<script>
var FALLBACK_IMAGES = [
  require('@/assets/images/qwenImage2Pro/32ccbe94-155c-4873-9e85-602969ecb6e4.webp'),
  require('@/assets/images/qwenImage2Pro/b4c88e78-6c88-4db5-9ea2-a6041609a380.webp'),
  require('@/assets/images/qwenImage2Pro/f9f87426-60d9-4f97-a372-7cb55b5bf7de.webp'),
  require('@/assets/images/qwenImage2Pro/9bf30d3e-5ec1-4180-9165-519f7b0765a4.webp'),
  require('@/assets/images/qwenImage2Pro/c4581d5b-f28a-4ae5-8bb2-b110c30d954c.webp'),
  require('@/assets/images/qwenImage2Pro/e2bd1179-48db-4ce6-a713-2a0a62dd5dd5.webp'),
  require('@/assets/images/qwenImage2Pro/3204b3e8-c5ca-40b4-afa9-a65f6dd806e2.webp')
]

var BATCH_IMAGES = [
  FALLBACK_IMAGES[4],
  require('@/assets/images/qwenImage2Pro/qwen-batch-lamp-02.webp'),
  require('@/assets/images/qwenImage2Pro/qwen-batch-lamp-03.webp'),
  require('@/assets/images/qwenImage2Pro/qwen-batch-lamp-04.webp')
]

var FORMAT_IMAGES = [
  require('@/assets/images/qwenImage2Pro/qwen-format-fashion.webp'),
  require('@/assets/images/qwenImage2Pro/qwen-format-paper-art.webp'),
  require('@/assets/images/qwenImage2Pro/qwen-format-music-room.webp')
]

module.exports = {
  name: 'QwenImageSeoContent',
  props: {
    features: { type: Array, default: function () { return [] } },
    useCases: { type: Array, default: function () { return [] } },
    faqs: { type: Array, default: function () { return [] } },
    howToSteps: { type: Array, default: function () { return [] } }
  },
  data: function () { return { fallbackImages: FALLBACK_IMAGES, formatImages: FORMAT_IMAGES } },
  computed: {
    batchImages: function () { return BATCH_IMAGES },
    galleryExamples: function () {
      var result = []
      var self = this
      this.features.forEach(function (feature, featureIndex) {
        var rows = self.examples(feature)
        if (rows.length) rows.forEach(function (row) { result.push(row) })
        else result.push({ prompt: self.featurePrompt(feature), image: self.featureImage(feature, featureIndex) })
      })
      return result.slice(0, 7)
    }
  },
  methods: {
    // 读取 Qwen Image 2.0 Pro 当前语言的专属页面文案。
    text: function (field) { return this.$t('seo.imageModels.qwenImage2Pro.' + field) },
    // 将单图和多行案例统一为提示词与图片数据。
    examples: function (feature) {
      if (!feature) return []
      var rows = feature.demo && feature.demo.rows ? feature.demo.rows : []
      if (rows.length) return rows
      return feature.image ? [{ prompt: feature.prompt || feature.short, image: feature.image }] : []
    },
    // 返回功能案例的首条完整提示词。
    featurePrompt: function (feature) {
      var rows = this.examples(feature)
      return rows.length ? rows[0].prompt : (feature.prompt || feature.short || feature.name)
    },
    // 返回功能图片，缺失时使用同序号的 Qwen 现有素材。
    featureImage: function (feature, index) {
      var rows = this.examples(feature)
      return rows.length ? rows[0].image : (feature.image || this.fallbackImages[index % this.fallbackImages.length])
    },
    // 从括号中读取负向提示词，没有时使用通用排除项。
    negativePrompt: function (prompt) {
      var match = prompt.match(/[（(](?:Negative|负向)[：:]?\s*([^）)]*)[）)]/i)
      return match ? match[1] : this.text('negativePromptFallback')
    },
    // 移除括号内负向提示词，保留主要创作描述。
    positivePrompt: function (prompt) { return prompt.replace(/\s*[（(](?:Negative|负向)[：:]?[^）)]*[）)]/i, '') },
    // 平滑定位到对应的 Qwen 功能区。
    scrollTo: function (id) {
      var target = document.getElementById(id)
      if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }
}
</script>

<style scoped>
.qwen-content { width: 100%; max-width: 1060px; margin: 0 auto; color: #171a21; }
.qwen-section { margin-bottom: 112px; scroll-margin-top: 88px; }
.qwen-feature-console { display: grid; grid-template-columns: .85fr 1.15fr; gap: 58px; padding: 66px; border-radius: 30px; background: radial-gradient(circle at 20% 10%, rgba(51,104,255,.28), transparent 35%), #111725; color: #fff; }
.qwen-console-copy > span, .qwen-section-title > span, .qwen-enhance-copy > span, .qwen-format-copy > span, .qwen-realism > div > span, .qwen-how-intro > span { color: #5f8dff; font-size: 11px; font-weight: 800; letter-spacing: .12em; text-transform: uppercase; }
.qwen-console-copy h2, .qwen-section-title h2, .qwen-enhance-copy h2, .qwen-format-copy h2, .qwen-realism h2, .qwen-how-intro h2 { margin: 12px 0 14px; font-size: clamp(29px, 3.5vw, 42px); line-height: 1.16; letter-spacing: -.04em; }
.qwen-console-copy p, .qwen-section-title p, .qwen-enhance-copy > p, .qwen-format-copy > p, .qwen-realism > div > p, .qwen-how-intro > p { margin: 0; color: #68707d; font-size: 14px; line-height: 1.75; }
.qwen-console-copy p { color: #b9c0cc; }
.qwen-console-copy button, .qwen-cta button { margin-top: 26px; padding: 13px 21px; border: 0; border-radius: 9px; background: #fff; color: #151925; cursor: pointer; font-size: 12px; font-weight: 750; }
.qwen-feature-list { border-top: 1px solid rgba(255,255,255,.12); }
.qwen-feature-list button { display: grid; width: 100%; grid-template-columns: 34px 1fr 20px; gap: 14px; padding: 18px 4px; border: 0; border-bottom: 1px solid rgba(255,255,255,.12); background: transparent; color: #fff; cursor: pointer; text-align: left; }
.qwen-feature-list b { color: #6f96ff; font-size: 11px; }
.qwen-feature-list span { display: flex; flex-direction: column; gap: 5px; }
.qwen-feature-list strong { font-size: 14px; }
.qwen-feature-list small { color: #929ba9; font-size: 11px; line-height: 1.5; }
.qwen-feature-list i { color: #6f96ff; font-style: normal; }
.qwen-section-title { max-width: 750px; margin-bottom: 35px; }
.qwen-section-title--center { margin-right: auto; margin-left: auto; text-align: center; }
.qwen-lab-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 18px; }
.qwen-lab-grid article { display: grid; grid-template-rows: auto 330px; overflow: hidden; border: 1px solid #dfe3ea; border-radius: 18px; background: #fff; }
.qwen-prompt-panel { padding: 24px; }
.qwen-prompt-panel label { display: block; margin-bottom: 6px; color: #9299a5; font-size: 9px; font-weight: 800; letter-spacing: .1em; text-transform: uppercase; }
.qwen-prompt-panel p { margin: 0 0 15px; color: #414751; font-size: 12px; line-height: 1.62; }
.qwen-prompt-panel p.negative { color: #b24b63; }
.qwen-prompt-panel button, .qwen-batch-prompt button, .qwen-gallery-track button { padding: 0; border: 0; background: transparent; color: #3469e8; cursor: pointer; font-size: 11px; font-weight: 750; }
.qwen-lab-grid figure { margin: 0; overflow: hidden; background: #edf0f4; }
.qwen-lab-grid img, .qwen-enhance-results img, .qwen-batch-grid img, .qwen-format-wall img, .qwen-gallery-track img { width: 100%; height: 100%; object-fit: cover; }
.qwen-enhance-section { display: grid; grid-template-columns: .82fr 1.18fr; gap: 54px; align-items: center; padding: 60px; border-radius: 28px; background: linear-gradient(145deg, #eef4ff, #f8faff); }
.qwen-short-prompt { margin-top: 26px; padding: 20px; border: 1px solid #dbe4f4; border-radius: 13px; background: #fff; }
.qwen-short-prompt small { display: block; margin-bottom: 9px; color: #8a93a1; font-size: 9px; font-weight: 800; text-transform: uppercase; }
.qwen-short-prompt strong { color: #303640; font-size: 14px; line-height: 1.6; }
.qwen-enhance-results { display: grid; grid-template-columns: repeat(2, 1fr); gap: 12px; }
.qwen-enhance-results figure { margin: 0; overflow: hidden; border-radius: 15px; background: #e7eaf0; }
.qwen-enhance-results img { aspect-ratio: 4 / 5; }
.qwen-enhance-results figcaption { padding: 12px; color: #59606c; background: #fff; font-size: 10px; line-height: 1.45; }
.qwen-batch-stage { display: grid; grid-template-columns: .55fr 1.45fr; gap: 18px; padding: 18px; border: 1px solid #dfe3e9; border-radius: 22px; background: #f5f7fa; }
.qwen-batch-prompt { display: flex; padding: 28px; flex-direction: column; align-items: flex-start; justify-content: center; border-radius: 15px; background: #fff; }
.qwen-batch-prompt > span { color: #9199a5; font-size: 10px; font-weight: 800; text-transform: uppercase; }
.qwen-batch-prompt p { margin: 17px 0; color: #424954; font-size: 13px; line-height: 1.7; }
.qwen-batch-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 8px; }
.qwen-batch-grid figure { position: relative; aspect-ratio: 16 / 10; margin: 0; overflow: hidden; border-radius: 12px; }
.qwen-batch-grid b, .qwen-gallery-track figure b { position: absolute; top: 10px; left: 10px; padding: 6px 8px; border-radius: 6px; background: rgba(15,20,31,.72); color: #fff; font-size: 9px; }
.qwen-format-section { display: grid; grid-template-columns: .72fr 1.28fr; gap: 58px; align-items: center; }
.qwen-size-list { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 25px; }
.qwen-size-list i { padding: 8px 10px; border: 1px solid #dfe3e9; border-radius: 8px; color: #59616e; font-size: 10px; font-style: normal; }
.qwen-format-wall { display: grid; height: 580px; grid-template-columns: 1.15fr .85fr; grid-template-rows: repeat(2, 1fr); gap: 10px; }
.qwen-format-wall figure { margin: 0; overflow: hidden; border-radius: 15px; background: #edf0f4; }
.qwen-format-wall figure:first-child { grid-row: 1 / 3; }
.qwen-realism { position: relative; min-height: 610px; overflow: hidden; border-radius: 28px; background: #e9edf2; }
.qwen-realism > img { position: absolute; width: 100%; height: 100%; object-fit: cover; }
.qwen-realism::after { content: ''; position: absolute; inset: 0; background: linear-gradient(90deg, rgba(10,14,23,.9), rgba(10,14,23,.38) 52%, transparent 75%); }
.qwen-realism > div { position: relative; z-index: 1; max-width: 430px; padding: 80px 64px; color: #fff; }
.qwen-realism > div > p { color: #c4cad4; }
.qwen-realism button { margin-top: 25px; padding: 0; border: 0; background: transparent; color: #8eafff; cursor: pointer; font-weight: 750; }
.qwen-how-to { display: grid; grid-template-columns: .82fr 1.18fr; gap: 74px; align-items: start; }
.qwen-how-intro { position: sticky; top: 105px; }
.qwen-timeline { position: relative; padding-left: 34px; border-left: 1px solid #dce1e8; }
.qwen-timeline article { display: grid; grid-template-columns: 48px 1fr; gap: 18px; padding: 0 0 42px; }
.qwen-timeline article > b { color: #4273ed; font-size: 21px; }
.qwen-timeline h3 { margin: 0 0 9px; font-size: 17px; }
.qwen-timeline p { margin: 0; color: #6d7480; font-size: 13px; line-height: 1.7; }
.qwen-timeline > button { padding: 12px 20px; border: 0; border-radius: 9px; background: #182034; color: #fff; cursor: pointer; font-weight: 750; }
.qwen-gallery-track { display: grid; grid-template-columns: repeat(3, 1fr); gap: 14px; }
.qwen-gallery-track article { overflow: hidden; padding-bottom: 18px; border: 1px solid #e0e4e9; border-radius: 15px; background: #fff; }
.qwen-gallery-track figure { position: relative; aspect-ratio: 4 / 3; margin: 0; overflow: hidden; }
.qwen-gallery-track p { min-height: 58px; margin: 16px 17px 12px; color: #4f5661; font-size: 11px; line-height: 1.55; }
.qwen-gallery-track button { margin-left: 17px; }
.qwen-use-cases { padding: 70px 60px; border-radius: 28px; background: #f3f5f8; }
.qwen-use-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 13px; }
.qwen-use-grid article { padding: 23px; border: 1px solid #dde2e8; border-radius: 14px; background: #fff; }
.qwen-use-grid span { color: #4a78e8; font-size: 10px; font-weight: 800; }
.qwen-use-grid h3 { margin: 23px 0 9px; font-size: 15px; }
.qwen-use-grid p { margin: 0; color: #717884; font-size: 12px; line-height: 1.65; }
.qwen-faq { max-width: 860px; margin-right: auto; margin-left: auto; }
.qwen-faq details { border-bottom: 1px solid #dfe3e8; }
.qwen-faq summary { display: flex; padding: 22px 2px; align-items: center; justify-content: space-between; gap: 18px; cursor: pointer; font-size: 15px; font-weight: 700; list-style: none; }
.qwen-faq summary::-webkit-details-marker { display: none; }
.qwen-faq summary i { position: relative; width: 14px; height: 14px; }
.qwen-faq summary i::before, .qwen-faq summary i::after { content: ''; position: absolute; top: 7px; left: 1px; width: 12px; height: 1px; background: #737b87; }
.qwen-faq summary i::after { transform: rotate(90deg); }
.qwen-faq details[open] summary i::after { transform: rotate(0); }
.qwen-faq details p { margin: -2px 0 22px; color: #68707c; font-size: 14px; line-height: 1.75; }
.qwen-cta { display: grid; min-height: 400px; grid-template-columns: 1.06fr .94fr; overflow: hidden; border-radius: 28px; background: #eaf0ff; }
.qwen-cta > div { align-self: center; padding: 58px; }
.qwen-cta > div > span { color: #3e6fe7; font-size: 10px; font-weight: 800; letter-spacing: .1em; text-transform: uppercase; }
.qwen-cta h2 { margin: 14px 0; font-size: clamp(29px, 3.5vw, 42px); line-height: 1.16; }
.qwen-cta p { margin: 0; color: #616977; font-size: 14px; line-height: 1.7; }
.qwen-cta button { background: #182034; color: #fff; }
.qwen-cta figure { position: relative; margin: 0; overflow: hidden; }
.qwen-cta figure > img { width: 100%; height: 100%; object-fit: cover; }
.qwen-cta figure > div { position: absolute; right: 18px; bottom: 18px; display: grid; grid-template-columns: repeat(4, 1fr); gap: 5px; }
.qwen-cta figure i { padding: 7px; border-radius: 6px; background: rgba(15,20,31,.72); color: #fff; font-size: 9px; font-style: normal; }
@media (max-width: 900px) {
  .qwen-feature-console, .qwen-enhance-section, .qwen-format-section, .qwen-how-to, .qwen-cta { grid-template-columns: 1fr; }
  .qwen-how-intro { position: static; }
  .qwen-gallery-track, .qwen-use-grid { grid-template-columns: repeat(2, 1fr); }
  .qwen-cta figure { min-height: 340px; }
}
@media (max-width: 640px) {
  .qwen-section { margin-bottom: 76px; }
  .qwen-feature-console, .qwen-enhance-section, .qwen-use-cases { margin-right: -18px; margin-left: -18px; padding: 46px 18px; border-radius: 0; }
  .qwen-lab-grid, .qwen-enhance-results, .qwen-batch-stage, .qwen-gallery-track, .qwen-use-grid { grid-template-columns: 1fr; }
  .qwen-batch-grid { grid-template-columns: repeat(2, 1fr); }
  .qwen-format-wall { height: 440px; }
  .qwen-realism { min-height: 560px; margin-right: -18px; margin-left: -18px; border-radius: 0; }
  .qwen-realism > div { padding: 60px 22px; }
  .qwen-realism::after { background: linear-gradient(90deg, rgba(10,14,23,.91), rgba(10,14,23,.48) 70%, transparent); }
  .qwen-timeline { padding-left: 18px; }
  .qwen-cta { grid-template-columns: 1fr; margin-right: -18px; margin-left: -18px; border-radius: 0; }
  .qwen-cta > div { padding: 44px 22px; }
}
</style>
